import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { EmptyPage } from 'shared/ui/empty-page'

import { useInsuranceDraft } from '../../model/insurance-draft'
import { getInsuranceAgeError } from '../../model/travelers'
import { InsuranceTripPurpose } from '../../model/types'
import { InsuranceLayout } from '../../ui/insurance-layout'

import { ApplicationSkeleton } from './ui/application-skeleton'
import { CountryCard } from './ui/country-card'
import { DatesCard } from './ui/dates-card'
import { PurposeCard } from './ui/purpose-card'
import { TravelersCard } from './ui/travelers-card'

import { useInsurancePurposes } from './model/use-insurance-purposes'
import s from './styles.module.scss'

interface ApplicationInsurancePageProps {
  purposes: InsuranceTripPurpose[] | null
  onPurposesLoaded: (purposes: InsuranceTripPurpose[]) => void
}

const ApplicationInsurancePage = ({
  purposes: cachedPurposes,
  onPurposesLoaded,
}: ApplicationInsurancePageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { draft, setPurpose, setTravelerAges, setInfantAges } =
    useInsuranceDraft()
  const { purposes, isLoading, isError, retry } = useInsurancePurposes(
    cachedPurposes,
    onPurposesLoaded,
  )

  useEffect(() => {
    if (draft.purpose) return

    const defaultPurpose = purposes.find(({ isDefault }) => isDefault)

    if (defaultPurpose) setPurpose(defaultPurpose)
  }, [draft.purpose, purposes, setPurpose])

  const travelerAgeErrors = draft.travelerAges.map((age) =>
    getInsuranceAgeError(age, 'adult'),
  )
  const infantAgeErrors = draft.infantAges.map((age) =>
    getInsuranceAgeError(age, 'infant'),
  )
  const hasAgeErrors = [...travelerAgeErrors, ...infantAgeErrors].some(Boolean)
  const hasApplicationData = Boolean(
    draft.country &&
    draft.startDate &&
    draft.endDate &&
    draft.purpose &&
    purposes.some(({ id }) => id === draft.purpose?.id) &&
    !hasAgeErrors,
  )

  return (
    <InsuranceLayout
      title={t('insurance.vzr.application.title')}
      onGoBack={() => navigate(-1)}
      footer={
        !isLoading && !isError
          ? {
              disabled: !hasApplicationData,
              onClick: () => navigate(RoutingPaths.insurance.program.path()),
              children: t('insurance.vzr.actions.continue'),
            }
          : undefined
      }
    >
      {isLoading ? (
        <ApplicationSkeleton />
      ) : isError ? (
        <div className="container">
          <EmptyPage
            title={t('insurance.vzr.errors.load_title')}
            subTitle={t('error_des')}
            buttonText={t('try_again')}
            onClick={retry}
          />
        </div>
      ) : (
        <div className={s.content}>
          <CountryCard
            country={draft.country}
            onSelect={() => navigate(RoutingPaths.insurance.country.path())}
          />
          <DatesCard
            startDate={draft.startDate}
            endDate={draft.endDate}
            onSelect={() => navigate(RoutingPaths.insurance.calendar.path())}
          />
          <TravelersCard
            travelerAges={draft.travelerAges}
            infantAges={draft.infantAges}
            travelerAgeErrors={travelerAgeErrors}
            infantAgeErrors={infantAgeErrors}
            onTravelerAgesChange={setTravelerAges}
            onInfantAgesChange={setInfantAges}
          />
          <PurposeCard
            purposes={purposes}
            purpose={draft.purpose}
            onChange={setPurpose}
          />
        </div>
      )}
    </InsuranceLayout>
  )
}

export default ApplicationInsurancePage
